import { useEffect, useRef } from 'react'

/**
 * Hook to handle infinite scroll for a scrollable container.
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.scrollRootRef - Reference to the scrollable container
 * @param {Function} options.loadMore - Async function to load more items
 * @param {boolean} options.hasMore - Boolean indicating if there are more items to load
 * @param {boolean} options.isLoading - Boolean indicating if items are currently being loaded
 */
export const useBottomInfiniteScroll = ({
  scrollRootRef,
  loadMore,
  hasMore,
  isLoading,
}) => {
  const sentinelRef = useRef(null)

  useEffect(() => {
    const root = scrollRootRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel) return

    const io = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore().catch(error => {
            console.error('Error loading more items:', error)
          })
        }
      },
      {
        root, // observe inside the scroll pane
        threshold: 0.1, // fire when 10 % visible
      },
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [scrollRootRef, hasMore, isLoading, loadMore])

  return sentinelRef
}
